import { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin, ChevronDown, Check, Search, X, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Location {
  id: string;
  name: string;
  isActive: boolean;
}

interface LocationSelectorProps {
  locations: Location[];
  selectedLocationId: string | undefined;
  onSelect: (id: string | undefined) => void;
  /** Show "Zurück zum Super Admin" option (super_admin only) */
  showBackToAdmin?: boolean;
}

const MAX_VISIBLE = 5;

export function LocationSelector({ locations, selectedLocationId, onSelect, showBackToAdmin = false }: LocationSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeLocations = locations.filter((l) => l.isActive);
  const selectedLocation = activeLocations.find((l) => l.id === selectedLocationId);
  const displayName = selectedLocation?.name || 'Alle Standorte';

  const filteredLocations = useMemo(() => {
    if (!search.trim()) return activeLocations;
    const q = search.toLowerCase();
    return activeLocations.filter((l) => l.name.toLowerCase().includes(q));
  }, [activeLocations, search]);

  // Show max 3 when no search, all matches when searching
  const visibleLocations = search.trim() ? filteredLocations : filteredLocations.slice(0, MAX_VISIBLE);
  const hasMore = !search.trim() && filteredLocations.length > MAX_VISIBLE;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Small delay so the DOM renders first
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  const handleSelect = (id: string | undefined) => {
    onSelect(id);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative hidden sm:block" ref={ref}>
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-200',
          'border border-slate-600/60 bg-slate-700/40 backdrop-blur-sm',
          'hover:bg-slate-700/70 hover:border-slate-500/80',
          isOpen && 'bg-slate-700/70 border-orange-500/50 ring-1 ring-orange-500/20'
        )}
      >
        <MapPin className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-slate-200 font-medium max-w-[180px] truncate">{displayName}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-slate-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 min-w-[260px] rounded-xl bg-slate-800 border border-slate-600/60 shadow-xl shadow-black/30 backdrop-blur-xl z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search */}
          <div className="px-3 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Standort suchen..."
                autoComplete="off"
                className="w-full pl-8 pr-8 py-1.5 bg-slate-700/60 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50 focus:border-orange-500/30"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* "Zurück zum Super Admin" option - shown when super_admin is in location view */}
          {!search.trim() && showBackToAdmin && selectedLocationId && (
            <>
              <button
                onClick={() => handleSelect(undefined)}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm transition-colors hover:bg-orange-500/10"
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-orange-500/20 text-orange-400">
                  <ArrowLeft className="w-3.5 h-3.5" />
                </div>
                <span className="flex-1 text-left truncate text-orange-300 font-medium">
                  Zurück zum Super Admin
                </span>
              </button>
              <div className="mx-3 my-1 border-t border-slate-700" />
            </>
          )}

          {/* Location list */}
          <div className="max-h-[180px] overflow-y-auto py-1">
            {visibleLocations.length > 0 ? (
              visibleLocations.map((location) => {
                const isSelected = location.id === selectedLocationId;
                return (
                  <button
                    key={location.id}
                    onClick={() => handleSelect(location.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3.5 py-2.5 text-sm transition-colors',
                      'hover:bg-slate-700/60',
                      isSelected && 'bg-orange-500/10'
                    )}
                  >
                    <div
                      className={cn(
                        'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                        isSelected
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-slate-700 text-slate-400'
                      )}
                    >
                      <MapPin className="w-3.5 h-3.5" />
                    </div>
                    <span
                      className={cn(
                        'flex-1 text-left truncate',
                        isSelected ? 'text-orange-300 font-medium' : 'text-slate-300'
                      )}
                    >
                      {location.name}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-orange-400 shrink-0" />}
                  </button>
                );
              })
            ) : (
              <div className="px-3.5 py-3 text-sm text-slate-500 text-center">
                Kein Standort gefunden
              </div>
            )}
          </div>

          {/* "More" hint */}
          {hasMore && (
            <div className="px-3.5 py-2 border-t border-slate-700 text-xs text-slate-500 text-center">
              +{filteredLocations.length - MAX_VISIBLE} weitere — Suche nutzen
            </div>
          )}
        </div>
      )}
    </div>
  );
}
